import { useEffect, useState } from "react";
import axios from "axios";
import { Ban, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CA_PROVINCES = [
  ["BC", "British Columbia"],
  ["AB", "Alberta"],
  ["SK", "Saskatchewan"],
  ["MB", "Manitoba"],
  ["ON", "Ontario"],
  ["QC", "Quebec"],
  ["NB", "New Brunswick"],
  ["NS", "Nova Scotia"],
  ["PE", "Prince Edward Island"],
  ["NL", "Newfoundland and Labrador"],
  ["YT", "Yukon"],
  ["NT", "Northwest Territories"],
  ["NU", "Nunavut"],
];

// NoRefundsBanner — user-approved copy shown BEFORE any digital checkout.
// Buyer must scroll past it before the "Pay" CTA is visible.
export const NoRefundsBanner = ({ isDigital = true }) => {
  if (!isDigital) return null;
  return (
    <div
      className="rounded-lg border border-[#F0C464]/40 bg-[#F0C464]/5 p-3 flex items-start gap-2"
      data-testid="no-refunds-banner"
    >
      <Ban className="w-4 h-4 text-[#F0C464] mt-0.5 flex-shrink-0" />
      <div className="text-[11px] text-[#8A92A0] leading-relaxed">
        <strong className="text-[#F0C464] block mb-1">All sales are final — digital products &amp; services</strong>
        All sales of digital products and services are <strong className="text-white">final and non-refundable</strong>. Exceptions will only be made if the digital file is confirmed to be defective, misrepresented, or if the initial download fails to complete. The refund period is within <strong className="text-white">7 days after the date of purchase</strong>. Please contact admin for assistance with these specific issues.
      </div>
    </div>
  );
};

// TaxAddressConfirm — mandatory before every checkout. Reads /tax/rate,
// pre-selects the IP-detected jurisdiction, but the buyer MUST tick the
// disclaimer + confirm the address before we hand back an applied rate.
export const TaxAddressConfirm = ({ sessionId, onConfirmed }) => {
  const [detected, setDetected] = useState(null);
  const [inCanada, setInCanada] = useState(true);
  const [region, setRegion] = useState("BC");
  const [country, setCountry] = useState("CA");
  const [acknowledge, setAcknowledge] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmedResult, setConfirmedResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tax/rate`);
        setDetected(data);
        if (data.detected_country === "CA") {
          setInCanada(true);
          setCountry("CA");
          if (data.detected_region) setRegion(data.detected_region);
        } else if (data.detected_country) {
          setInCanada(false);
          setCountry(data.detected_country);
        }
      } catch (_e) { /* silent — buyer picks manually */ }
    })();
  }, []);

  const confirm = async () => {
    if (!acknowledge) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/tax/confirm-address`, {
        country_code: inCanada ? "CA" : country,
        region_code: inCanada ? region : null,
        session_id: sessionId,
        acknowledged_disclaimer: true,
      });
      setConfirmedResult(data);
      onConfirmed?.(data);
    } catch (e) {
      // Surface error inline
      setConfirmedResult({ error: e.response?.data?.detail || "Confirmation failed" });
    } finally {
      setSaving(false);
    }
  };

  if (confirmedResult && !confirmedResult.error) {
    return (
      <div className="rounded-lg border border-[#8DB87A]/30 bg-[#8DB87A]/5 p-3" data-testid="tax-confirmed-banner">
        <p className="text-[11px] text-[#8DB87A] font-bold uppercase tracking-widest">Location confirmed</p>
        <p className="text-xs text-white mt-1">
          {confirmedResult.applied_tax_label} — <strong>{(confirmedResult.applied_tax_rate * 100).toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}%</strong> tax will be applied at checkout.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#EC008C]/40 bg-[#EC008C]/5 p-4 space-y-3" data-testid="tax-confirm-panel">
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 text-[#EC008C] mt-0.5 flex-shrink-0" />
        <div className="text-[11px] text-[#8A92A0] leading-relaxed">
          <strong className="text-[#EC008C] block mb-1">Confirm your location</strong>
          For Canadian Tax law compliance, we need to know your location. If you are outside of Canada, no tax will be charged. Any false confirmation of address will be subject to Canadian laws.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setInCanada(true)}
          className={`h-10 rounded-lg border text-sm transition ${inCanada ? "border-[#EC008C] bg-[#EC008C]/10 text-white" : "border-[#1A2030] text-[#8A92A0] hover:border-[#EC008C]/40"}`}
          data-testid="tax-in-canada-btn"
        >
          I&apos;m in Canada
        </button>
        <button
          type="button"
          onClick={() => setInCanada(false)}
          className={`h-10 rounded-lg border text-sm transition ${!inCanada ? "border-[#EC008C] bg-[#EC008C]/10 text-white" : "border-[#1A2030] text-[#8A92A0] hover:border-[#EC008C]/40"}`}
          data-testid="tax-outside-canada-btn"
        >
          Outside Canada (0% tax)
        </button>
      </div>

      {inCanada ? (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#8A92A0] mb-1 block">Province / Territory</label>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="h-10 w-full px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
            data-testid="tax-province-select"
          >
            {CA_PROVINCES.map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
        </div>
      ) : (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#8A92A0] mb-1 block">Country (ISO 2-letter)</label>
          <input
            type="text"
            maxLength={2}
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
            placeholder="US"
            className="h-10 w-full px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none uppercase"
            data-testid="tax-country-input"
          />
        </div>
      )}

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={acknowledge}
          onChange={(e) => setAcknowledge(e.target.checked)}
          className="mt-1 accent-[#EC008C]"
          data-testid="tax-ack-checkbox"
        />
        <span className="text-[11px] text-[#8A92A0]">
          I confirm the location above is accurate. I understand that any false confirmation of address is subject to Canadian laws.
        </span>
      </label>

      <button
        type="button"
        onClick={confirm}
        disabled={!acknowledge || saving}
        className="w-full h-10 rounded-lg bg-[#EC008C] text-black font-bold text-sm hover:bg-[#FF3399] disabled:opacity-60"
        data-testid="tax-confirm-btn"
      >
        {saving ? "Confirming…" : "Confirm location + continue"}
      </button>

      {confirmedResult?.error && (
        <p className="text-[11px] text-[#EC008C]" data-testid="tax-confirm-error">{confirmedResult.error}</p>
      )}

      {detected?.detected_country && (
        <p className="text-[10px] text-[#4A5060]" data-testid="tax-detected-hint">
          Detected: {detected.detected_country}{detected.detected_region ? `, ${detected.detected_region}` : ""} (via IP — you can override)
        </p>
      )}
    </div>
  );
};
